import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import api from "../api/axios";

export const CartContext = createContext();

export const useCart = () => {
  const ctx = React.useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within CartProvider");
  }
  return ctx;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const userIdRef = useRef(null);

  // Load cart from backend or localStorage
  const loadCart = useCallback(async (userId) => {
    setInitialized(false);

    if (userId) {
      // When logged in, start empty and fetch from backend
      setCart([]);
      try {
        const res = await api.get("/cart");
        if (res.data?.success && Array.isArray(res.data.cart?.items)) {
          const cleaned = res.data.cart.items.map(item => ({
            ...item,
            id: item.productId,
            price: Number(String(item.price).replace(/,/g, "")) || 0,
          }));
          setCart(cleaned);
          localStorage.setItem("appCart", JSON.stringify(cleaned));
          setInitialized(true);
          return;
        }
      } catch (err) {
        // API failed — show empty rather than stale data
        setCart([]);
        localStorage.removeItem("appCart");
      }
    } else {
      // Not logged in — load from localStorage
      const loadFromLocal = () => {
        const saved = localStorage.getItem("appCart");
        if (saved) {
          try {
            return JSON.parse(saved).map(item => ({
              ...item,
              price: Number(String(item.price).replace(/,/g, "")) || 0,
            }));
          } catch {
            return [];
          }
        }
        return [];
      };
      setCart(loadFromLocal());
    }
    setInitialized(true);
  }, []);

  // Initial load
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    userIdRef.current = user?._id || null;
    loadCart(userIdRef.current);
  }, [loadCart]);

  // Re-load when user changes (login/logout in same tab)
  useEffect(() => {
    const checkUser = () => {
      const user = JSON.parse(localStorage.getItem("user"));
      const newId = user?._id || null;
      if (newId !== userIdRef.current) {
        userIdRef.current = newId;
        loadCart(newId);
      }
    };

    window.addEventListener("storage", checkUser);
    window.addEventListener("focus", checkUser);
    window.addEventListener("user-login", checkUser);

    return () => {
      window.removeEventListener("storage", checkUser);
      window.removeEventListener("focus", checkUser);
      window.removeEventListener("user-login", checkUser);
    };
  }, [loadCart]);

  // Persist to localStorage whenever cart changes (after init)
  useEffect(() => {
    if (initialized) {
      localStorage.setItem("appCart", JSON.stringify(cart));
    }
  }, [cart, initialized]);

  const cleanItem = (item) => ({
    ...item,
    qty: item.qty ?? 1,
    price: Number(String(item.price).replace(/,/g, "")) || 0,
  });

  // ADD TO CART
  const addToCart = useCallback((rawItem) => {
    const item = cleanItem(rawItem);

    setCart((prev) => {
      const exists = prev.find((x) => x.id === item.id);
      if (exists) {
        return prev.map((x) =>
          x.id === item.id ? { ...x, qty: x.qty + 1 } : x
        );
      }
      return [...prev, item];
    });

    // Sync to backend outside of setCart updater
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?._id) {
      const userToken = localStorage.getItem("token") || localStorage.getItem("accessToken");
      if (!userToken) {
        console.warn("Cart sync skipped: no auth token found");
      } else {
        api.post("/cart/add", {
          productId: item.id,
          title: item.title,
          price: item.price,
          imageUrl: item.imageUrl || "",
          qty: 1,
        }).then((res) => {
          if (!res.data?.success) console.warn("Cart sync response not ok:", res.data);
        }).catch((err) => {
          console.error("Cart sync failed:", err?.response?.status, err?.response?.data || err?.message);
        });
      }
    }
  }, []);

  // UPDATE QTY
  const updateQty = useCallback((id, qty) => {
    if (qty <= 0) {
      setCart((prev) => {
        const updated = prev.filter((x) => x.id !== id);
        setTimeout(async () => {
          try { await api.delete(`/cart/item/${id}`); } catch (e) {}
        }, 0);
        return updated;
      });
      return;
    }
    setCart((prev) => {
      const updated = prev.map((x) =>
        x.id === id ? { ...x, qty } : x
      );
      setTimeout(async () => {
        try { await api.put(`/cart/qty/${id}`, { qty }); } catch (e) {}
      }, 0);
      return updated;
    });
  }, []);

  // REMOVE
  const removeItem = useCallback((id) => {
    setCart((prev) => {
      const updated = prev.filter((x) => x.id !== id);
      setTimeout(async () => {
        try { await api.delete(`/cart/item/${id}`); } catch (e) {}
      }, 0);
      return updated;
    });
  }, []);

  // CLEAR CART
  const clearCart = useCallback(async () => {
    setCart([]);
    try {
      await api.delete("/cart");
    } catch (err) {
      console.error("Clear cart sync failed:", err);
    }
  }, []);

  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);
  const cartTotal = cart.reduce((sum, item) => {
    const numericPrice = Number(String(item.price).replace(/,/g, "")) || 0;
    return sum + numericPrice * item.qty;
  }, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        updateQty,
        removeItem,
        clearCart,
        cartCount,
        cartTotal,
        initialized,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};